import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";

import { sort } from "@/lib/sort";
import { novaClient } from "@/lib/trpc";
import { CertificationType } from "@/types";

// All certification CRUD in one hook. Writes change the profile served by the
// sheet-data query, so each mutation updates that cache in place at the hook
// level — every sheet view stays in sync even when the calling editor
// unmounts before the mutation resolves.
export function useCertification() {
  const queryClient = useQueryClient();

  const setSheetDataCertifications = (
    userId: number,
    updater: (certifications: CertificationType[]) => CertificationType[],
  ) => {
    queryClient.setQueriesData(
      {
        predicate: (query) => {
          const [, params] = query.queryKey as [
            unknown,
            { input?: { userId?: number } }?,
          ];
          return params?.input?.userId === userId;
        },
        queryKey: getQueryKey(
          novaClient.internal.user.active.getCandidacySheetData,
        ),
      },
      (cached: unknown) => {
        if (!cached) return cached;
        const data = cached as { certifications?: CertificationType[] };
        // Re-sort so a created or re-dated row lands where the server would
        // have put it instead of at the end of the list.
        return {
          ...data,
          certifications: sort.sortCertifications([
            ...updater(data.certifications ?? []),
          ]),
        };
      },
    );
  };

  const createCertification =
    novaClient.internal.user.profile.certification.create.useMutation({
      onSuccess: (created, variables) => {
        setSheetDataCertifications(variables.userId, (certifications) => [
          ...certifications,
          created as CertificationType,
        ]);
      },
    });

  const updateCertification =
    novaClient.internal.user.profile.certification.update.useMutation({
      onSuccess: (updated, variables) => {
        const updatedCertification = updated as CertificationType;
        setSheetDataCertifications(variables.userId, (certifications) =>
          certifications.map((certification) =>
            certification.id === updatedCertification.id
              ? { ...certification, ...updatedCertification }
              : certification,
          ),
        );
      },
    });

  const deleteCertification =
    novaClient.internal.user.profile.certification.delete.useMutation({
      onSuccess: (_deleted, variables) => {
        setSheetDataCertifications(variables.userId, (certifications) =>
          certifications.filter(
            (certification) => certification.id !== variables.id,
          ),
        );
      },
    });

  return { createCertification, deleteCertification, updateCertification };
}
